import apiClient from './authClient';
import { API_ENDPOINTS } from './authConfig';
import type {
  PaymentRequest,
  PaymentResponse,
  PaymentTransaction,
  CreditUsage,
  CreditsApiResponse,
  PaymentIntentResponse,
  PaymentMethodsResponse,
} from '../../types/credits';

/**
 * Credits API service
 */
export const creditsApi = {
  // Get organization credits and usage information
  getOrgCredits: async (orgId: string): Promise<CreditsApiResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.CREDITS.ORG_CREDITS(orgId));
    return response.data;
  },

  // Refresh organization credits (GET /organizations/{id}/credits with cache bypass)
  refreshOrgCredits: async (orgId: string): Promise<CreditsApiResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.CREDITS.ORG_CREDITS(orgId), {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return response.data;
  },

  // Get available payment methods
  getPaymentMethods: async (): Promise<PaymentMethodsResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.CREDITS.PAYMENT_METHODS);
    return response.data;
  },

  // Create a payment intent
  createPaymentIntent: async (
    orgId: string,
    paymentRequest: PaymentRequest
  ): Promise<PaymentIntentResponse> => {
    const response = await apiClient.post(
      API_ENDPOINTS.CREDITS.PAYMENT_INTENT(orgId),
      paymentRequest
    );
    return response.data;
  },

  // Confirm a payment (for Stripe)
  confirmPayment: async (paymentId: string, paymentMethodId?: string): Promise<PaymentResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.CREDITS.PAYMENT_CONFIRM(paymentId), {
      payment_method_id: paymentMethodId,
    });
    return response.data;
  },

  // Get payment status
  getPaymentStatus: async (paymentId: string): Promise<PaymentResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.CREDITS.PAYMENT_STATUS(paymentId));
    return response.data;
  },

  // Get payment transactions history
  getPaymentHistory: async (
    orgId: string,
    page: number = 1,
    limit: number = 10,
    status?: string
  ): Promise<{
    transactions: PaymentTransaction[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status) {
      params.append('status', status);
    }

    const response = await apiClient.get(
      `${API_ENDPOINTS.CREDITS.PAYMENT_HISTORY(orgId)}?${params}`
    );
    return response.data;
  },

  // Cancel a pending payment
  cancelPayment: async (paymentId: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CREDITS.PAYMENT_CANCEL(paymentId));
  },

  // Get credit usage details
  getCreditUsage: async (orgId: string, period: string = '30d'): Promise<CreditUsage> => {
    const response = await apiClient.get(
      `${API_ENDPOINTS.CREDITS.CREDIT_USAGE(orgId)}?period=${period}`
    );
    return response.data;
  },

  // Webhook endpoint for payment status updates (for backend integration)
  handlePaymentWebhook: async (payload: unknown, signature: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CREDITS.PAYMENT_WEBHOOK, payload, {
      headers: {
        'X-Webhook-Signature': signature,
      },
    });
  },
};

// Export individual functions for convenience
export const {
  getOrgCredits,
  refreshOrgCredits,
  getPaymentMethods,
  createPaymentIntent,
  confirmPayment,
  getPaymentStatus,
  getPaymentHistory,
  cancelPayment,
  getCreditUsage,
  handlePaymentWebhook,
} = creditsApi;

export default creditsApi;
